export default {
  name: "Job Opportunity and Supporting Documentation",
  sections: [
    {
      fields: [
        {
          code: "E.1/2",
          machine_name: "e2_soc_title",
          widget: "text",
          label: "SOC Occupation Title",
          validate: ["required"],
          hidden: true,
          tooltip: `Enter the occupational title associated with the SOC/O*NET (Occupational Employment and Wage Statistics Survey (OEWS)) code. For example, the occupational title associated with SOC/O*NET code 45-2092 is “Farmworkers and Laborers, Crop, Nursery, and Greenhouse.” The entry in this field should be the same as the SOC title entered by the SWA on the Form ETA-790, if available, and may include the more specific O*NET code title, if applicable.`,
        },
        {
          code: "E.1/2",
          machine_name: "e1_soc_code",
          widget: "soc",
          label: "SOC Code",
          field_to_modify: "e2_soc_title",
          validate: ["required"],
          tooltip: `Enter the six digit Standard Occupational Classification (SOC) or eight digit Occupational Network (O*NET) code for the occupation, which most clearly describes the work to be performed. For example, the six digit SOC code for a fruit or vegetable harvester or orchard worker is 45-2092 (Farmworkers and Laborers, Crop, Nursery, and Greenhouse). If O*NET includes an eight digit code for the occupation, the employer may enter the more specific O*NET code. The entry in this field should be the same as the SOC code entered by the State Workforce Agency (SWA) on the Form ETA-790, if available, and may include the more specific O*NET code, if applicable.`,
        },
        {
          isGroup: {
            hidden: true,
            code: "E.3",
            machine_name: "e3_is_job_order_complete",
            label: "Form ETA-790/790A Job Order",
            fields: [
              {
                widget: "radio",
                choice: "Yes",
                value: "YES",
              },
              {
                widget: "radio",
                choice: "No",
                value: "NO",
              },
            ],
            tooltip: `Mark “Yes” to indicate that the employer is submitting a copy of the completed job order (Form ETA-790/790A) with this application. Reminder: The job order must satisfy all requirements listed at 20 CFR 653, subpart F, and 20 CFR 655, subpart B`,
          },
        },
        // V2
        {
          isGroup: {
            code: "E.5",
            machine_name: "e4_is_joint_employer_complete",
            label:
              "If “Joint Employer” is marked in question A.1, the Form ETA-790A and Addendum B identify the name(s), address(es), total number of workers needed, and crops and agricultural work of each employer that will employ workers.",
            tooltip: `If the employer marked “Joint Employer” in Section A, Question 1, mark “Yes” to indicate that the job order (Form ETA-790A and Addendum B) identifies the name(s) and address(es) of each employer that will employ any worker under the job order associated with this application, the total number of workers each employer needs, and the crops and agricultural work of each employer.  If “Individual Employer,” “Association – Sole Employer,” or “Association – Agent” is marked in Section A, Question 1a, mark “N/A” here.`,
            coloring: "grey",
            isVisible: {
              when: "a1_employer_type||a1_employer_type_a formVersionId",
              is: [
                ["joint", "association-joint"],
                [2, 3],
              ],
              operator: "compareMultiValuesOrConditional",
            },
            warning: {
              text: `Joint employers and Associations filing as a joint employer must identify on the Form ETA-790A the name, address, total number of workers needed, and the crops and agricultural work to be performed, each employer that will employ H-2A workers.`,
              isVisible: {
                when: "e4_is_joint_employer_complete",
                is: "NO",
                operator: "===",
              },
            },
            fields: [
              {
                widget: "radio",
                choice: "Yes",
                value: "YES",
                validate: ["required"],
              },
              {
                widget: "radio",
                choice: "No",
                value: "NO",
                validate: ["required"],
                isVisible: {
                  when: "formVersionId",
                  is: 1,
                },
              },
              {
                widget: "radio",
                choice: "N/A",
                value: "N/A",
                validate: ["required"],
                isVisible: {
                  when: "formVersionId",
                  is: [2, 3],
                  operator: "||",
                },
              },
            ],
          },
        },
        {
          isGroup: {
            isVisible: {
              when: "a2_is_contractor",
              is: "YES",
            },
            warning: {
              text: `H-2A Labor Contractors must identify on the Form ETA-790A the name and location of each fixed-site agricultural business to which the H-2ALC expects to provide H-2A workers, the expected beginning and ending dates when the H-2ALC will be providing the workers to each fixed site, and a description of the crops and activities the workers are expected to perform at such fixed site.`,
              isVisible: {
                when: "e5_is_work_locations_complete",
                is: "NO",
                operator: "===",
              },
            },
            code: "E.6",
            machine_name: "e5_is_work_locations_complete",
            label:
              "The Form ETA-790A, Addendum B, identifies the name(s) and location(s) of each fixed-site agricultural business the employer will be providing H-2A workers, the expected first and last dates of work for each business, and a description of crops and activities the workers will perform.",
            tooltip: `If “Yes” is marked in Section A, Question 2, indicating the employer is operating as an H-2ALC related to this application, mark “Yes” here to indicate that the job order (Form ETA-790A, Addendum B) identifies the name(s) and location(s) of each fixed-site agricultural business where the worker(s) will perform labor or services, the expected first and last dates of work for each fixed-site agricultural business, and a description of the crops and activities the worker(s) will perform.`,
            coloring: "none",
            fields: [
              {
                widget: "radio",
                choice: "Yes",
                value: "YES",
                validate: [
                  {
                    valueChecker: "e5_is_work_locations_complete",
                    message: "This field is required",
                    needsFieldValues: true,
                    extraData: {
                      valueOf: "a2_is_contractor",
                      check: "valueCheck",
                    },
                  },
                ],
              },
              {
                widget: "radio",
                choice: "No",
                value: "NO",
                validate: ["required"],
              },
            ],
          },
        },
        {
          code: "E.7",
          machine_name: "e6_is_work_contracts_complete",
          label:
            "A copy of fully-executed work contract(s) with each fixed-site agricultural business identified on the Form ETA-790A, Addendum B, is attached to this application.",
          tooltip: `If “Yes” is marked in Section A, Question 2, indicating the employer is operating as an H-2ALC related to this application, mark “Yes” here to indicate that the employer is submitting with this application a copy of fully-executed work contract(s) with each fixed-site agricultural business identified on the job order (Form ETA-790A, Addendum B) where the worker(s) will perform labor or services.`,
          coloring: "none",
          name: "H-2ALC Work Contracts",
          widget: "file_upload",
          fileTypes: [".pdf", ".png", ".jpg"],
          validate: ["required", "checkValue"],
          isVisible: {
            when: "a2_is_contractor",
            is: "YES",
          },
          warning: {
            text: `H-2A Labor Contractors must upload copies of the fully-executed work contracts with each fixed-site agricultural business identified on the Form ETA-790A.`,
            isVisible: {
              when: "e6_is_work_contracts_complete",
              is: "NO",
              operator: "===",
            },
          },
        },
        {
          isGroup: {
            isVisible: {
              when: "a2_is_contractor",
              is: "YES",
            },
            warning: {
              text: `IMPORTANT - A NOTICE OF DEFICIENCY WILL BE ISSUED ON THIS APPLICATION. As an H-2A Labor Contractor and unless you qualify for an exemption, you are required by 20 CFR 655.132 to provide the Department with a copy of your MSPA Certificate of Registration identifying the farm labor contracting activities you are authorized to perform under this application.`,
              isVisible: {
                when: "e7_is_mspa_complete",
                is: "NO",
                operator: "===",
              },
            },
            code: "E.8",
            machine_name: "e7_is_mspa_complete",
            label:
              "A copy of the employer’s valid MSPA Certificate of Registration identifying the farm labor contracting activities the employer is authorized to perform is attached to this application.",
            tooltip: `If “Yes” is marked in Section A, Question 2, indicating the employer is operating as an H-2ALC related to this application, mark “Yes” here to indicate that the employer is submitting a copy of the employer’s valid MSPA FLC Certificate of Registration with this application. If the employer is not subject to MSPA (i.e., is not required to obtain a MSPA FLC Certificate of Registration due to an applicable exemption as found in MSPA Section 4(a) and corresponding regulations at 29 C.F.R. 500.30) for the work described in the Form ETA-790A, mark “N/A.” Reminder: If required under MSPA at 29 U.S.C. 1801 et seq., a Certificate of Registration identifying the specific farm labor contracting activities the H-2ALC is authorized to perform as an FLC must be submitted.`,
            coloring: "none",
            fields: [
              {
                widget: "radio",
                choice: "Yes",
                value: "YES",
                clearFiles: ["e7_is_mspa_complete_file"],
                validate: ["required"],
              },
              {
                widget: "radio",
                choice: "No",
                value: "NO",
                clearFiles: ["e7_is_mspa_complete_file"],
                validate: ["required"],
              },
              {
                widget: "radio",
                choice: "N/A",
                value: "N/A",
                clearFiles: ["e7_is_mspa_complete_file"],
                validate: ["required"],
              },
            ],
          },
        },
        {
          code: "E.8",
          machine_name: "e7_is_mspa_complete_file",
          widget: "file_upload",
          name: "H-2ALC MSPA Certificate",
          fileTypes: [".pdf", ".png", ".jpg"],
          validate: ["required", "checkValue"],
          isVisible: {
            when: "e7_is_mspa_complete",
            is: "YES",
          },
          reviewHidden: true,
        },

        // V2
        {
          code: "E.9",
          machine_name: "e8_is_surety_bond_complete",
          label:
            "A signed and dated <a href='https://flag.dol.gov/sites/default/files/2024-08/Form_ETA-9142A_Appendix_B_surety_bond.pdf' rel='noopener noreferrer' target='_blank'>Appendix B, H-2A Labor Contractor Surety Bond</a>, for the employer identified in Section B of this application is attached.",
          tooltip: `If "Yes" is marked in Section A, Question 2, indicating the employer is operating as an H-2ALC related to this application, mark "Yes" here to indicate that the employer is submitting a signed and dated surety bond using Appendix B, H-2A Labor Contractor Surety Bond, for the employer identified in Section B, as required by 20 CFR 655.132(c), with this application.`,
          coloring: "none",
          name: "Surety Bond",
          widget: "file_upload",
          fileTypes: [".pdf", ".png", ".jpg"],
          validate: ["required", "checkValue"],
          isVisible: {
            when: "a2_is_contractor formVersionId",
            is: [["YES"], [2, 3]],
            operator: "compareMultiFieldValuesAND_ORInArray",
          },
          warning: {
            text: `H-2A Labor Contractors must upload a surety bond that clearly identifies the issuer, the name, address, phone number, and contact person for the surety, and provides the amount of the bond (as calculated pursuant to 29 CFR 501.9) and any identifying designation used by the surety for the bond.`,
            isVisible: {
              when: "e8_is_surety_bond_complete",
              is: "NO",
              operator: "===",
            },
          },
        },
        // V1
        // {
        //   code: "E.8",
        //   machine_name: "e8_is_surety_bond_complete",
        //   label:
        //     "A signed and dated Appendix B, H-2A Labor Contractor Surety Bond, for the employer identified in Section B of this application is attached.",
        //   tooltip: `If “Yes” is marked in Section A, Question 2, indicating the employer is operating as an H-2ALC related to this application, mark “Yes” here to indicate that the employer is submitting a signed and dated surety bond using Appendix B, H-2A Labor Contractor Surety Bond, for the employer identified in Section B, as required by 20 CFR 655.132(c), with this application.`,
        //   coloring: "none",
        //   name: "Surety Bond",
        //   widget: "file_upload",
        //   fileTypes: [".pdf", ".png", ".jpg"],
        //   validate: ["required", "checkValue"],
        //   isVisible: {
        //     when: "a2_is_contractor formVersionId",
        //     is: [["YES"], [1]],
        //     operator: "compareMultiFieldValuesAND_ORInArray",
        //   },
        //   warning: {
        //     text: `H-2A Labor Contractors must upload a surety bond that clearly identifies the issuer, the name, address, phone number, and contact person for the surety, and provides the amount of the bond (as calculated pursuant to 29 CFR 501.9) and any identifying designation used by the surety for the bond.`,
        //     isVisible: {
        //       when: "e8_is_surety_bond_complete",
        //       is: "NO",
        //       operator: "===",
        //     },
        //   },
        // },
        {
          isGroup: {
            isVisible: {
              when: "a2_is_contractor",
              is: "YES",
            },
            warning: {
              text: `To avoid any delays in processing your application, please make sure the Addendum B, Form ETA-790A identifies each fixed-site agricultural businesses that will provide housing and/or transportation to workers between the worksite and the living quarters and that you submit documentation demonstrating that housing and/or transportation meet applicable standards.`,
              isVisible: {
                when: "e9_is_housing_transportation",
                is: "YES",
                operator: "===",
              },
            },
            code: "E.10",
            machine_name: "e9_is_housing_transportation",
            label:
              "Will any of the fixed-site agricultural businesses provide workers with housing and/or transportation between the place of employment and the living quarters under this application?",
            tooltip: `If “Yes” is marked in Section A, Question 2, indicating the employer is operating as an H-2ALC related to this application, mark “Yes” or “No” here to indicate whether any of the fixed-site agricultural businesses provide the worker(s) with housing and/or transportation between the place of employment and the living quarters.`,
            coloring: "none",
            fields: [
              {
                widget: "radio",
                choice: "Yes",
                value: "YES",
                validate: ["required"],
              },
              {
                widget: "radio",
                choice: "No",
                value: "NO",
                validate: ["required"],
              },
            ],
          },
        },
        {
          isGroup: {
            code: "E.11",
            machine_name: "foreignLaborRecEngagement",
            label:
              "Is the employer, and its attorney or agent, as applicable, engaging or planning to engage any agent(s) or recruiter(s) in the recruitment of prospective H-2A workers, regardless of whether such agent(s) or recruiter(s) is (are) located in the U.S. or abroad?",
            tooltip:
              'Mark "Yes" if the employer has engaged or is planning to engage any agent(s) or foreign labor recruiter(s), directly or indirectly, in the recruitment of prospective H-2A workers, regardless of whether such agent(s) or recruiter(s) is (are) located in the U.S. or abroad, Mark “No” here if the employer has not engaged and has no plans to engage any agent(s) or recruiter(s) in the recruitment of prospective H-2A workers.',
            coloring: "none",
            fields: [
              {
                widget: "radio",
                choice: "Yes",
                value: "YES",
                validate: ["required"],
                setSchema: [
                  { set: "appendixCAttached", to: "YES" },
                  { set: "appendixDAttached", to: "YES" },
                ],
              },
              {
                widget: "radio",
                choice: "No",
                value: "NO",
                validate: ["required"],
                clearFiles: ["foreignLaborRecContracts"],
                clearSchema: ["foreignLaborRecInfo"],
                setSchema: [
                  { set: "appendixCAttached", to: "YES" },
                  { set: "appendixDAttached", to: "N/A" },
                ],
              },
            ],
          },
        },
        {
          code: "E.11.a",
          machine_name: "foreignLaborRecContracts",
          label:
            "Indicate whether a copy of all agreements with any agent or recruiter whom you are engaging or planning to engage in the recruitment of H-2A workers is attached to this application.",
          tooltip: `If “Yes” in Section A, question 2, indicating the employer is operating as an H-2ALC related to this application, mark “Yes” or “No” to indicate whether the employer is submitting an original surety bond, as required by 20 CFR 655.132(b)(3), with this application.`,
          coloring: "none",
          name: "Recruiter Agreements",
          widget: "file_upload",
          fileTypes: [".pdf", ".png", ".jpg"],
          validate: ["required", "checkValue"],
          isVisible: {
            when: "foreignLaborRecEngagement",
            is: "YES",
          },
          warning: {
            text: `<b><em>Important Note:</em></b> The employer must retain documentation to support the employer’s response to this question and be prepared to submit such documentation in response to a NOD from the Certifying Officer (CO) prior to issuing a final temporary agricultural labor certification decision, or in the event of an audit or investigation. 20 CFR 655.167(c)(8).`,
            isVisible: {
              when: "foreignLaborRecContracts",
              is: "NO",
            },
          },
        },
        {
          code: "I5",
          machine_name: "files",
          widget: "text",
          hidden: true,
        },
      ],
    },
  ],
};
